import { useId, useMemo, useState } from "react";
import { copyText } from "@/lib/copy";
import { applyStyle, decorate, decoratorsBySource, stylesByCategory, FANCY_STYLES, DECORATORS } from "@/lib/text-generators";

/**
 * The flagship font generator: type once, see every style grouped by category,
 * tap any line to copy. A live filter narrows by style name or keyword. All
 * transforms are pure + deterministic (SSR-safe).
 */
export function FontGenerator({ defaultValue = "Fancy Text" }: { defaultValue?: string }) {
  const [input, setInput] = useState(defaultValue);
  const [filter, setFilter] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [decorateId, setDecorateId] = useState<string | null>(null);
  // Surprise = a random lettering style × random decorator. Picked only on click
  // (never during render) so there's no SSR/hydration mismatch.
  const [surprise, setSurprise] = useState<{ styleId: string; decoId: string } | null>(null);
  const inputId = useId();
  const filterId = useId();

  const lettering = useMemo(() => FANCY_STYLES.filter((s) => s.category !== "symbols"), []);
  function rollSurprise() {
    const st = lettering[Math.floor(Math.random() * lettering.length)];
    const d = DECORATORS[Math.floor(Math.random() * DECORATORS.length)];
    setSurprise({ styleId: st.id, decoId: d.id });
  }

  const q = filter.trim().toLowerCase();
  const groups = useMemo(() => {
    const all = stylesByCategory();
    if (!q) return all;
    return all
      .map((g) => ({
        category: g.category,
        styles: g.styles.filter(
          (s) =>
            s.name.toLowerCase().includes(q) ||
            s.category.includes(q) ||
            s.keywords.some((k) => k.toLowerCase().includes(q)),
        ),
      }))
      .filter((g) => g.styles.length > 0);
  }, [q]);

  const matchCount = useMemo(() => groups.reduce((n, g) => n + g.styles.length, 0), [groups]);

  async function copy(id: string, value: string) {
    const ok = await copyText(value, { source: "generator-line", formatKey: id });
    if (!ok) return;
    setCopiedId(id);
    window.setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1200);
  }

  const surpriseStyle = surprise ? FANCY_STYLES.find((s) => s.id === surprise.styleId) : undefined;
  const surpriseDeco = surprise ? DECORATORS.find((d) => d.id === surprise.decoId) : undefined;
  const surpriseValue =
    surpriseStyle && surpriseDeco ? decorate(surpriseDeco, applyStyle(surpriseStyle, input)) : "";

  return (
    <div className="mt-8">
      <label htmlFor={inputId} className="block text-sm font-medium text-foreground/90">
        Your text
      </label>
      <textarea
        id={inputId}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Type to see every font style…"
        rows={2}
        spellCheck={false}
        className="mt-2 w-full resize-y rounded-lg border border-border bg-surface/60 px-3 py-2.5 font-mono text-base text-foreground placeholder:text-muted-foreground focus-visible:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />

      {/* Surprise me — one random style × random decoration. */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={rollSurprise}
          className="inline-flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span aria-hidden="true">✨</span> Surprise me
        </button>
        <span className="text-xs text-muted-foreground">a random font style, decorated</span>
      </div>

      {surpriseStyle && surpriseDeco && (
        <div className="mt-3 rounded-xl border border-primary/30 bg-primary/5 p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {surpriseStyle.name} · {surpriseDeco.source}
            </span>
            <button
              type="button"
              onClick={rollSurprise}
              aria-label="Shuffle again"
              className="rounded-md border border-border bg-surface/60 px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span aria-hidden="true">🎲</span> Again
            </button>
          </div>
          <button
            type="button"
            onClick={() => copy("surprise", surpriseValue)}
            aria-label="Copy the surprise result"
            className="group mt-2 flex w-full items-center justify-between gap-3 rounded-lg text-left focus-visible:outline-none"
          >
            <span className="fancy-output min-w-0 flex-1 break-words text-xl leading-relaxed text-foreground sm:text-2xl">
              {surpriseValue}
            </span>
            <span
              className={`flex-none rounded border border-border bg-surface/60 px-2 py-1 text-[10px] uppercase tracking-wider ${
                copiedId === "surprise" ? "text-accent" : "text-muted-foreground group-hover:text-foreground"
              }`}
              aria-hidden="true"
            >
              {copiedId === "surprise" ? "Copied" : "Copy"}
            </span>
          </button>
        </div>
      )}

      {/* Filter + category jump-nav. Sticky so it stays reachable while scrolling. */}
      <div className="sticky top-2 z-10 mt-4 rounded-xl border border-border bg-background/85 p-3 backdrop-blur">
        <label htmlFor={filterId} className="sr-only">
          Filter font styles
        </label>
        <input
          id={filterId}
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={`Filter ${FANCY_STYLES.length} styles — try "cursive", "bubble", "bold"…`}
          className="w-full rounded-md border border-border bg-surface/60 px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
        />
        {!q && (
          <nav className="mt-2 flex flex-wrap gap-1.5" aria-label="Jump to category">
            {groups.map((g) => (
              <a
                key={g.category.id}
                href={`#cat-${g.category.id}`}
                className="rounded-full border border-border bg-surface/60 px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
              >
                {g.category.name}
              </a>
            ))}
          </nav>
        )}
        {q && (
          <p className="mt-2 text-xs text-muted-foreground">
            {matchCount} {matchCount === 1 ? "style" : "styles"} match “{filter}”.
          </p>
        )}
      </div>

      <div className="mt-6 space-y-10">
        {groups.map((g) => (
          <section key={g.category.id} id={`cat-${g.category.id}`} aria-labelledby={`h-${g.category.id}`} className="scroll-mt-24">
            <div className="mb-3">
              <h2 id={`h-${g.category.id}`} className="font-display text-xl font-semibold">
                {g.category.name}
              </h2>
              <p className="text-sm text-muted-foreground">{g.category.blurb}</p>
            </div>
            <ul className="space-y-2">
              {g.styles.map((s) => {
                const value = applyStyle(s, input);
                const isCopied = copiedId === s.id;
                const isEmpty = value.length === 0;
                // Frame/symbol styles are already decorations — don't offer to
                // decorate a decoration.
                const canDecorate = s.category !== "symbols";
                const isOpen = decorateId === s.id;
                return (
                  <li key={s.id}>
                    <div className="group flex w-full items-stretch gap-2">
                      <button
                        type="button"
                        onClick={() => copy(s.id, value)}
                        disabled={isEmpty}
                        aria-label={`Copy ${s.name} style`}
                        title={s.keywords.join(", ")}
                        className="glass flex min-w-0 flex-1 items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-60"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
                            {s.name}
                          </span>
                          <span className="fancy-output mt-1 block break-words text-base leading-relaxed text-foreground sm:text-lg">
                            {isEmpty ? <span className="text-muted-foreground">…</span> : value}
                          </span>
                        </span>
                        <span
                          className={`flex-none rounded border border-border bg-surface/60 px-2 py-1 text-[10px] uppercase tracking-wider ${
                            isCopied ? "text-accent" : "text-muted-foreground group-hover:text-foreground"
                          }`}
                          aria-hidden="true"
                        >
                          {isCopied ? "Copied" : "Copy"}
                        </span>
                      </button>
                      {canDecorate && (
                        <button
                          type="button"
                          onClick={() => setDecorateId((d) => (d === s.id ? null : s.id))}
                          disabled={isEmpty}
                          aria-expanded={isOpen}
                          aria-label={`Decorate ${s.name} style`}
                          className={`glass flex flex-none items-center gap-1 rounded-lg px-3 text-xs font-medium transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${
                            isOpen ? "border-primary/50 text-foreground" : "text-muted-foreground"
                          }`}
                        >
                          <span aria-hidden="true">✨</span>
                          <span className="hidden sm:inline">Decorate</span>
                        </button>
                      )}
                    </div>
                    {isOpen && <DecoratePanel styleId={s.id} styledValue={value} />}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * Inline "Decorate" panel: wraps the already-styled text with every decorator,
 * grouped by source (Symbols / Frames / Kaomoji / Decorative). Tap any to copy.
 * The decorators are curated from the site's /categories, /decorative and
 * /kaomoji content — so a font style and a decoration compose into one string.
 */
function DecoratePanel({ styleId, styledValue }: { styleId: string; styledValue: string }) {
  const [copied, setCopied] = useState<string | null>(null);
  const groups = decoratorsBySource();

  async function copyDeco(decoId: string, value: string) {
    const ok = await copyText(value, { source: "generator-line", formatKey: `${styleId}+${decoId}` });
    if (!ok) return;
    setCopied(decoId);
    window.setTimeout(() => setCopied((c) => (c === decoId ? null : c)), 1200);
  }

  return (
    <div className="mt-2 rounded-lg border border-dashed border-border bg-surface/30 p-3">
      <p className="mb-2 text-xs text-muted-foreground">
        Decorate <span className="fancy-output break-words text-foreground">{styledValue}</span> — tap any to copy
      </p>
      <div className="max-h-[22rem] space-y-3 overflow-auto pr-1">
        {groups.map((grp) => (
          <div key={grp.source}>
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{grp.label}</h3>
            <ul className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2">
              {grp.items.map((d) => {
                const value = decorate(d, styledValue);
                const isCopied = copied === d.id;
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => copyDeco(d.id, value)}
                      aria-label={`Copy ${grp.label} decoration`}
                      className="group flex w-full items-center justify-between gap-2 rounded-md border border-border bg-surface/50 px-2.5 py-1.5 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="fancy-output min-w-0 flex-1 break-words text-sm leading-relaxed text-foreground">{value}</span>
                      <span
                        className={`flex-none text-[10px] uppercase tracking-wider ${
                          isCopied ? "text-accent" : "text-muted-foreground group-hover:text-foreground"
                        }`}
                        aria-hidden="true"
                      >
                        {isCopied ? "✓" : "copy"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
